// ─── PAIC (People + AI Collaboration) guided tour ─────────────────────────────
export const TOUR_MODULE_KEY = "people-plus-ai";

// Cross-page tour phases (namespaced) — used by the later Group Agents handoff.
export const CROSS_PAGE_TOUR_PHASE = {
  GROUP_AGENTS: "people-plus-ai:group-agents", // show Stats/Version/Doc steps there
  FINISH: "people-plus-ai:finish", // back on the dashboard, resume at Finish
};

export const TOUR_STEPS = [
  {
    // Welcome — centered greeting
    state: "always",
    title: "Welcome to People + AI",
    description:
      "This is where you identify, prioritize, and assign AI agents to collaborate with your workforce. Let's take a quick tour so you know exactly where to start.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    state: "data",
    key: "rolesTable",
    title: "Filter to Select Roles",
    description:
      "Use the column filters (Role Type, Business, Location, Function, Sub-Function, Job Level) to select the exact fields you want to analyze. The selected options will be included in your PAIC group.",
    placement: "top",
  },
  {
    // Shown only when the roles table is empty (replaces the table steps).
    state: "empty",
    key: "emptyState",
    title: "No PAIC Groups Yet",
    description:
      "You don't have any PAIC groups yet. Filter the roles table down to the set you want to analyze, name a group, and run your first evaluation.",
    placement: "top",
  },
  {
    state: "always",
    key: "groupControl",
    title: "Create or Select a Group",
    description:
      "Once you've selected the options you want, type the group name and press Enter to begin the process — or pick an existing group from the dropdown. Creating a group is the prerequisite step — everything else in PAIC becomes available once a group exists and evaluation is complete.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "evaluate",
    title: "Evaluate PAIC Potential",
    description:
      'Once you\'ve named your group, click Evaluate PAIC Potential to run the evaluation. SkillsBridge.AI identifies the AI agents that could support your selected roles and calculates their potential impact — typically ready in 1-2 hours depending on group size. Once the analysis is complete the status will update to "Completed". You can check progress any time from the "Select or create group" dropdown.',
    placement: "bottom",
  },
  {
    state: "data",
    key: "manageGroups",
    title: "Manage Groups",
    description:
      "The Manage Groups button allows you to see every group you own, groups delegated, and shared — each in its own tab with a count badge. Here you can share groups with a colleague, delegate co-creator access, transfer ownership, or archive a group.",
    placement: "bottom",
  },
  {
    state: "data",
    key: "viewPaic",
    title: "View PAIC Potential",
    description:
      'Once a group\'s status is "Completed", the button becomes "View PAIC Potential" — click it to see every AI agent identified to include AI Potential, roles and positions supported, potential value, cost, and ROI. Use the View By switch to flip between agents or seeing the roles they support.',
    placement: "bottom",
  },
  {
    // On the separate Group Agents route (added in a later pass).
    state: "data",
    key: "statsPanel",
    handoffToPage: true,
    title: "Stats Summary Panel",
    description:
      "This panel updates in real time as you check and uncheck agents. Group Total shows the combined impact across every identified agent; Selected Agents shows the same metrics — Potential Value, ROI, Roles, Positions, AI Agents, AI Potential, Est. Cost — scoped to just the agents you've currently checked.",
    placement: "left",
  },
  {
    state: "data",
    key: "versionControls",
    handoffToPage: true,
    title: "Select Version & Submit",
    description:
      "Check the agents you want to include, then click Submit to save that selection as a Version. Want to compare different combinations — like a smaller starter set vs. the full group? Create a few versions and switch between them anytime using the Select Version dropdown.",
    placement: "top",
  },
  {
    state: "data",
    key: "docType",
    handoffToPage: true,
    title: "Agent Documentation",
    description:
      "Once a version is processed, this dropdown shows you everything SkillsBridge generated for that iteration — covering your whole selected group of agents: org designs, agent architectures, job descriptions, product requirement documents, and the updated job descriptions for the human roles they work alongside.",
    placement: "top",
  },
  {
    // Finish — centered greeting. Primary focuses the group name/select control.
    state: "always",
    title: "You're all set!",
    description: "Ready to model AI's impact on your workforce?",
    primaryText: "Create PAIC Group Now",
    secondaryText: "Done",
  },
];

// Active steps for the current branch, preserving spec order.
export const getTourSteps = (isEmpty) =>
  TOUR_STEPS.filter(
    (step) => step.state === "always" || step.state === (isEmpty ? "empty" : "data"),
  );

// The cross-page steps, shown by the Group Agents route (used by the later pass).
export const GROUP_AGENTS_TOUR_STEPS = TOUR_STEPS.filter((step) => step.handoffToPage);
